import {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import {Check, ChevronRight, X} from 'lucide-react';
import {useAuth} from '../../modules/auth';
import {
  AUCTION_ROUTE,
  MANAGE_HOST,
  PROFILE_ROUTE,
  STRIPE_CONNECT,
  TABLES_ROUTE,
} from '../../Helpers/constants';
import {DashboardData} from '../../pages/Admin';
import {DiibsIcon} from '../common/DiibsIcon';

const DISMISSED_KEY = 'onboardingChecklistDismissed';

interface Props {
  dashboardData: DashboardData | null;
}

interface SubItem {
  id: string;
  label: string;
  completed: boolean;
  link: string;
}

interface ChecklistItem {
  id: string;
  label: string;
  completed: boolean;
  link: string;
  subItems?: SubItem[];
}

const OnboardingChecklist = ({dashboardData}: Props) => {
  const {currentUser, restaurantModule, auctionModule} = useAuth();
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(DISMISSED_KEY) === 'true'
  );
  const [hasHosts, setHasHosts] = useState<boolean | null>(null);
  const [hasTables, setHasTables] = useState<boolean | null>(null);
  const [hasAuctions, setHasAuctions] = useState<boolean | null>(null);
  const [checksReady, setChecksReady] = useState(false);

  useEffect(() => {
    const runChecks = async () => {
      const [hostsResult, tablesResult, auctionsResult] = await Promise.all([
        restaurantModule?.getHostDetails(),
        restaurantModule?.getRestaurantTables({pageNo: 1, pageSize: 1}),
        auctionModule?.getAuctions({pageNo: 1, pageSize: 1}),
      ]);
      setHasHosts(hostsResult?.success === true && (hostsResult.data?.length ?? 0) > 0);
      setHasTables(
        tablesResult?.success === true &&
          (tablesResult.data?.rows?.length ?? tablesResult.data?.count ?? 0) > 0
      );
      setHasAuctions(
        auctionsResult?.success === true &&
          (auctionsResult.data?.rows?.length ?? auctionsResult.data?.count ?? 0) > 0
      );
      setChecksReady(true);
    };
    runChecks();
  }, []);

  const profileSubItems: SubItem[] = [
    {
      id: 'mobile',
      label: 'Add mobile number',
      completed: currentUser?.mobileNumberAdded === true,
      link: PROFILE_ROUTE,
    },
    {
      id: 'stripe',
      label: 'Connect Stripe',
      completed: Number(currentUser?.isStripeConnect) === 1,
      link: STRIPE_CONNECT,
    },
    {
      id: 'image',
      label: 'Add profile image',
      completed: !!currentUser?.profilePicture,
      link: PROFILE_ROUTE,
    },
  ];

  const profileComplete = profileSubItems.every((s) => s.completed);

  const items: ChecklistItem[] = [
    {
      id: 'profile',
      label: 'Complete your venue profile',
      completed: profileComplete,
      link: PROFILE_ROUTE,
      subItems: profileSubItems,
    },
    {
      id: 'table',
      label: 'Add a table',
      completed: hasTables === true,
      link: TABLES_ROUTE,
    },
    {
      id: 'auction',
      label: 'Create your first auction or reservation',
      completed: hasAuctions === true,
      link: AUCTION_ROUTE,
    },
    {
      id: 'host',
      label: 'Invite a team member',
      completed: hasHosts === true,
      link: MANAGE_HOST,
    },
  ];

  const completedCount = items.filter((i) => i.completed).length;
  const allComplete = completedCount === items.length;

  if (dismissed) return null;
  if (!checksReady || dashboardData === null) return null;
  if (allComplete) return null;

  const handleDismiss = () => {
    localStorage.setItem(DISMISSED_KEY, 'true');
    setDismissed(true);
  };

  return (
    <div className='card mb-5 mb-xl-8'>
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bold fs-3 mb-1'>Welcome to DIIBS!</span>
          <span className='text-muted fw-semibold fs-7'>
            Complete these steps to start accepting bookings &mdash; {completedCount} of{' '}
            {items.length} done
          </span>
        </h3>
        <div className='card-toolbar'>
          <button
            className='btn btn-sm btn-icon btn-active-light-primary'
            onClick={handleDismiss}
            title='Dismiss'
          >
            <DiibsIcon icon={X} size={16} />
          </button>
        </div>
      </div>

      <div className='card-body py-4'>
        {items.map((item) => (
          <div key={item.id} className='mb-5'>
            <div className='d-flex align-items-center gap-3'>
              <span
                className={`d-flex align-items-center justify-content-center rounded-circle flex-shrink-0 ${
                  item.completed ? 'bg-success' : 'bg-light-primary'
                }`}
                style={{width: 24, height: 24}}
              >
                {item.completed ? (
                  <DiibsIcon icon={Check} size={13} className='text-white' />
                ) : (
                  <span className='text-primary fw-bold fs-8'>○</span>
                )}
              </span>

              <div className='d-flex align-items-center justify-content-between flex-grow-1'>
                {item.completed ? (
                  <span className='fw-semibold fs-6 text-muted text-decoration-line-through'>
                    {item.label}
                  </span>
                ) : (
                  <Link to={item.link} className='fw-semibold fs-6 text-dark text-hover-primary'>
                    {item.label}
                  </Link>
                )}
                {!item.completed && (
                  <DiibsIcon icon={ChevronRight} size={16} className='text-muted' />
                )}
              </div>
            </div>

            {item.subItems && !item.completed && (
              <div className='ms-9 mt-3 d-flex flex-column gap-2'>
                {item.subItems.map((sub) => (
                  <div key={sub.id} className='d-flex align-items-center gap-2'>
                    <span
                      className={`d-flex align-items-center justify-content-center rounded-circle flex-shrink-0 ${
                        sub.completed ? 'bg-success' : 'border border-2 border-light-primary'
                      }`}
                      style={{width: 18, height: 18}}
                    >
                      {sub.completed && (
                        <DiibsIcon icon={Check} size={10} className='text-white' />
                      )}
                    </span>
                    {sub.completed ? (
                      <span className='fs-7 text-muted text-decoration-line-through'>
                        {sub.label}
                      </span>
                    ) : (
                      <Link to={sub.link} className='fs-7 text-dark text-hover-primary'>
                        {sub.label}
                      </Link>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export {OnboardingChecklist};
